import React, { useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  BulkExpenseResult,
  BulkExpenseRow,
  BulkExpenseRowStatus
} from '../../../../../shared/models/unit-types.model';
import { ExpensesCategoryType } from '../../../../../shared/ui-models/unit-type.ui-model';
import { createBulkExpenses, previewBulkExpenses } from '../../../../shared/services/data.service';
import { getModuleName } from '../../../../shared/services/expenses.service';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

export interface BulkExpenseModalProps {
  showModal: boolean;
  closeHandler: () => void;
  createdHandler: (result: BulkExpenseResult) => void;
}

type EditableStatus = Exclude<BulkExpenseRowStatus, 'matched'>;
type BulkExpenseModalRow = BulkExpenseRow & { originalStatus?: EditableStatus };

interface RoomOption {
  value: string;
  label: string;
  unitTypeId: number;
}

export const BULK_EXPENSE_ISSUES: Record<string, string> = {
  invalid_format: 'Missing unit code',
  not_found: 'No matching room',
  missing_description: 'Missing description',
  invalid_amount: 'Invalid amount',
  invalid_date: 'Invalid date, use YYYY-MM-DD',
  invalid_category: 'Unknown category',
  duplicate: 'Repeated in this file',
  already_imported: 'Already imported',
  receipt_unsupported_link: 'Receipt must be a Google Drive link',
  receipt_not_shared: 'Receipt is not shared with "Anyone with the link"',
  receipt_too_large: 'Receipt is larger than 10 MB',
  receipt_unreachable: 'Receipt could not be downloaded'
};

const TEMPLATE_HEADERS = [
  'Unit Code',
  'Description',
  'Amount',
  'Date (YYYY-MM-DD)',
  'Category',
  'Receipt URL'
];

const CATEGORY_LABELS: Record<string, string> = {
  [ExpensesCategoryType.MAINTENANCE]: 'Maintenance',
  [ExpensesCategoryType.OTHERS]: 'Others',
  [ExpensesCategoryType.HOUSE_KEEPING]: 'House Keeping',
  [ExpensesCategoryType.LAUNDRY]: 'Laundry',
  [ExpensesCategoryType.INTERNET_AND_LANDLINE]: 'Internet and Landline',
  [ExpensesCategoryType.UNIT_PURCHASES]: 'Unit Purchases'
};

const amountFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const isReady = (row: BulkExpenseModalRow) => row.status === 'matched' && !row.issues.length;

const downloadTemplate = () => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(
    new Blob([`${TEMPLATE_HEADERS.join(',')}\r\n`], { type: 'text/csv' })
  );
  link.setAttribute('download', 'expenses-template.csv');
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(link.href);
  document.body.removeChild(link);
};

const cellInputClassName =
  'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 focus:border-[#185B43] focus:outline-none focus:ring-4 focus:ring-[#185B43]/10';

const roomSelectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 42,
    borderRadius: 12,
    borderColor: state.isFocused ? '#185B43' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(24, 91, 67, 0.1)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#185B43' : '#94a3b8' }
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 14,
    backgroundColor: state.isSelected
      ? '#185B43'
      : state.isFocused
      ? '#f0fdf4'
      : base.backgroundColor
  }),
  singleValue: (base: any) => ({
    ...base,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 14,
    fontWeight: 600
  })
};

export const BulkExpenseModal: React.FC<BulkExpenseModalProps> = ({
  showModal,
  closeHandler,
  createdHandler
}) => {
  const [file, setFile] = useState<File>();
  const [rows, setRows] = useState<BulkExpenseModalRow[] | null>(null);
  const [roomOptions, setRoomOptions] = useState<RoomOption[] | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const readyRows = (rows || []).filter(isReady);
  const issueRows = (rows || []).filter((row) => !isReady(row));
  const totalAmount = readyRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const canValidate = !isBusy && !!file;
  const canImport =
    !isBusy &&
    readyRows.length > 0 &&
    readyRows.every(
      (row) =>
        Number(row.amount) > 0 && !!row.paymentDate && !!row.category && !!row.description.trim()
    );

  const reset = () => {
    setFile(undefined);
    setRows(null);
    setIsBusy(false);
  };

  const cancel = () => {
    reset();
    closeHandler();
  };

  const selectFile = (selected?: File) => {
    if (selected && !selected.name.toLowerCase().endsWith('.csv')) {
      birdToast.error('Only CSV files are supported. Save the sheet as CSV first');
      return;
    }
    setFile(selected);
    setRows(null);
  };

  const updateRow = (index: number, changes: Partial<BulkExpenseModalRow>) => {
    setRows((current) =>
      (current || []).map((row) => (row.index === index ? { ...row, ...changes } : row))
    );
  };

  const loadRoomOptions = () => {
    if (roomOptions) {
      return;
    }
    getAllUnitTypes(getModuleName(), 0, -1, {}).then((response) => {
      if (isErrorInfo(response)) {
        birdToast.error('Failed to load rooms');
        return;
      }
      setRoomOptions(
        (response.unitList || [])
          .flatMap((unitType) =>
            (unitType.units || []).map((unit) => ({
              value: unit.unitId,
              label: unit.shortName,
              unitTypeId: Number(unitType.id)
            }))
          )
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    });
  };

  const assignRoom = (row: BulkExpenseModalRow, option: RoomOption | null) => {
    const originalStatus = row.originalStatus || (row.status as EditableStatus);
    updateRow(
      row.index,
      option
        ? {
            status: 'matched',
            originalStatus,
            roomId: option.value,
            roomName: option.label,
            unitTypeId: option.unitTypeId
          }
        : {
            status: originalStatus,
            originalStatus: undefined,
            roomId: null,
            roomName: null,
            unitTypeId: null
          }
    );
  };

  const renderRoomSelect = (row: BulkExpenseModalRow) => (
    <Select<RoomOption, false>
      aria-label="Room"
      menuPortalTarget={document.body}
      styles={roomSelectStyles}
      options={roomOptions || []}
      value={(roomOptions || []).find((option) => option.value === row.roomId) || null}
      isClearable
      isLoading={!roomOptions}
      placeholder="Select room"
      onChange={(option) => assignRoom(row, option as RoomOption | null)}
    />
  );

  const validate = () => {
    if (!file) {
      return;
    }
    setIsBusy(true);
    const promise = previewBulkExpenses(file)
      .then((response) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        setRows(response);
        if (response.some((row) => row.status !== 'matched')) {
          loadRoomOptions();
        }
        return response;
      })
      .finally(() => setIsBusy(false));

    birdToast.promise(promise, {
      loadingMessage: 'Validating expenses and checking receipts...',
      successMessage: 'Validation complete',
      errorFailedMessage: 'Failed to validate the expenses file',
      errorPermissionMessage: 'You do not have permission to create expenses'
    });
  };

  const importValid = () => {
    setIsBusy(true);
    const promise = createBulkExpenses(
      readyRows.map((row) => ({
        index: row.index,
        roomId: row.roomId || '',
        description: row.description.trim(),
        amount: Number(row.amount),
        paymentDate: Number(row.paymentDate),
        category: row.category || '',
        receiptUrl: row.receiptUrl || undefined
      }))
    ).then((response) => {
      if (isErrorInfo(response)) {
        setIsBusy(false);
        throw response;
      }
      reset();
      createdHandler(response);
      return response;
    });

    birdToast.promise(promise, {
      loadingMessage: `Creating ${readyRows.length} expenses and uploading receipts...`,
      successMessage: 'Expenses created',
      errorFailedMessage: 'Failed to create expenses',
      errorPermissionMessage: 'You do not have permission to create expenses'
    });
  };

  return (
    <Modal
      config={{
        showModal,
        fullScreen: false,
        maxWidth: 'lg',
        contentClassName: 'flex min-h-0 flex-1 flex-col',
        paperClassName:
          '!flex !flex-col !overflow-hidden !rounded-3xl !border !border-slate-200 !shadow-[0_24px_60px_-12px_rgba(15,23,42,0.25)]'
      }}
      eventHandlers={{ closeModalHandler: cancel }}
    >
      <section
        aria-labelledby="bulk-expense-title"
        className="flex min-h-0 flex-1 flex-col bg-white text-slate-900 antialiased"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#185B43] text-white">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                viewBox="0 0 24 24"
              >
                <path
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2
              id="bulk-expense-title"
              className="text-3xl font-bold tracking-tight text-slate-900"
            >
              Bulk Expenses
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close dialog"
            onClick={cancel}
            className="rounded-full p-2.5 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-8 py-6">
          {!rows && (
            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-700 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <p className="font-mono text-slate-900">{TEMPLATE_HEADERS.join(' · ')}</p>
                <p>Categories: {Object.values(CATEGORY_LABELS).join(', ')}.</p>
                <p>
                  Receipt URL is a Google Drive link shared with &ldquo;Anyone with the link&rdquo;.
                  Paste the link itself, not a &ldquo;View Receipt&rdquo; hyperlink, since CSV drops
                  the link behind it.
                </p>
              </div>
              <button
                type="button"
                onClick={downloadTemplate}
                className="shrink-0 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-[#185B43] shadow-sm hover:bg-green-50"
              >
                Download template
              </button>
            </div>
          )}

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              selectFile(event.dataTransfer.files?.[0]);
            }}
            className={`flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
              isDragging
                ? 'border-[#185B43] bg-green-50'
                : 'border-slate-300 bg-slate-50 hover:border-slate-400'
            }`}
          >
            {file ? (
              <div className="flex w-full max-w-lg items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-[#185B43]">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      clipRule="evenodd"
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                    />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-slate-900" title={file.name}>
                    {file.name}
                  </p>
                  <p className="text-sm text-slate-600">{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-full px-3 py-1.5 text-sm font-semibold text-[#185B43] hover:bg-green-50"
                >
                  Replace
                </button>
                <button
                  type="button"
                  aria-label="Remove file"
                  onClick={() => selectFile(undefined)}
                  className="rounded-full p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-white text-[#185B43] shadow-sm">
                  <svg
                    className="h-7 w-7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-base text-slate-700">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="font-semibold text-[#185B43] underline underline-offset-4 hover:text-[#134935]"
                  >
                    Choose expenses CSV
                  </button>{' '}
                  or drop it here
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => {
                selectFile(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </div>

          {rows && (
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
                <p className="text-sm font-semibold text-emerald-800">Ready</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-900">
                  {readyRows.length}
                </p>
              </div>
              <div
                className={`rounded-2xl border px-5 py-4 ${
                  issueRows.length ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p
                  className={`text-sm font-semibold ${
                    issueRows.length ? 'text-rose-800' : 'text-slate-700'
                  }`}
                >
                  Skipped
                </p>
                <p
                  className={`text-2xl font-bold tabular-nums ${
                    issueRows.length ? 'text-rose-900' : 'text-slate-900'
                  }`}
                >
                  {issueRows.length}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
                <p className="text-sm font-semibold text-slate-700">Total amount</p>
                <p className="text-2xl font-bold tabular-nums text-slate-900">
                  {amountFormatter.format(totalAmount)}
                </p>
              </div>
            </div>
          )}

          {issueRows.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-rose-200">
              <div className="flex items-center gap-2 bg-rose-50 px-5 py-3">
                <svg
                  className="h-5 w-5 text-rose-700"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h4 className="text-base font-semibold text-rose-900">
                  {issueRows.length} {issueRows.length === 1 ? 'row' : 'rows'} will be skipped
                </h4>
              </div>
              <ul className="divide-y divide-rose-100 bg-white">
                {issueRows.map((row) => (
                  <li key={row.index} className="flex items-center gap-4 px-5 py-3">
                    <span className="w-16 shrink-0 text-sm font-semibold text-slate-700">
                      Row {row.line}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-sm text-slate-900">
                        {row.unitCode || '—'}
                      </span>
                      <span
                        className="block truncate text-sm text-slate-600"
                        title={row.description}
                      >
                        {row.description || '—'}
                        {row.amount ? ` · ${amountFormatter.format(row.amount)}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-sm font-semibold text-rose-800">
                      {[row.status !== 'matched' ? row.status : null, ...row.issues]
                        .filter((issue): issue is string => !!issue)
                        .map((issue) => (
                          <span key={issue} className="block">
                            {BULK_EXPENSE_ISSUES[issue] || issue}
                          </span>
                        ))}
                    </span>
                    {row.status !== 'matched' && !row.issues.length && (
                      <div className="w-56 shrink-0">{renderRoomSelect(row)}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {readyRows.length > 0 && (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[960px] text-left text-base text-slate-800">
                <thead className="bg-slate-50 text-sm font-semibold text-slate-700">
                  <tr>
                    <th className="px-4 py-3">Room</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {readyRows.map((row) => (
                    <tr key={row.index} className="border-t border-slate-200">
                      <td className="whitespace-nowrap px-4 py-3">
                        {row.originalStatus ? (
                          <div className="w-56">{renderRoomSelect(row)}</div>
                        ) : (
                          <span className="inline-flex rounded-lg bg-green-50 px-2.5 py-1 font-mono text-sm font-semibold text-green-900">
                            {row.roomName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          aria-label="Description"
                          className={`${cellInputClassName} ${
                            row.description.trim() ? '' : '!border-rose-500'
                          }`}
                          value={row.description}
                          onChange={(event) =>
                            updateRow(row.index, { description: event.target.value })
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <select
                          aria-label="Category"
                          className={`${cellInputClassName} w-48`}
                          value={row.category || ''}
                          onChange={(event) =>
                            updateRow(row.index, { category: event.target.value })
                          }
                        >
                          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          aria-label="Amount"
                          type="number"
                          min={0}
                          className={`${cellInputClassName} w-32 tabular-nums ${
                            Number(row.amount) > 0 ? '' : '!border-rose-500'
                          }`}
                          value={row.amount ?? ''}
                          onChange={(event) =>
                            updateRow(row.index, {
                              amount: event.target.value === '' ? null : Number(event.target.value)
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <ReactDatePicker
                          className={`${cellInputClassName} w-36 ${
                            row.paymentDate ? '' : '!border-rose-500'
                          }`}
                          selected={row.paymentDate ? new Date(row.paymentDate) : null}
                          onChange={(date) =>
                            updateRow(row.index, {
                              paymentDate: date ? (date as Date).getTime() : null
                            })
                          }
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        {row.receiptUrl ? (
                          <a
                            href={row.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#185B43] underline underline-offset-4"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-slate-500">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <footer className="flex shrink-0 flex-col-reverse items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-8 py-5 sm:flex-row">
          <button
            type="button"
            onClick={cancel}
            className="rounded-full px-5 py-2.5 text-base font-semibold text-slate-800 transition-colors hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canValidate}
            onClick={validate}
            className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Validate CSV
          </button>
          <button
            type="button"
            disabled={!canImport}
            onClick={importValid}
            className="flex items-center justify-center gap-2 rounded-full bg-[#185B43] px-6 py-2.5 text-base font-semibold text-white shadow-md shadow-[#185B43]/20 transition-all hover:bg-[#134935] focus:outline-none focus:ring-2 focus:ring-[#185B43]/50 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none"
          >
            <span>
              {issueRows.length > 0
                ? `Add ${readyRows.length} valid only`
                : `Add ${readyRows.length} ${readyRows.length === 1 ? 'expense' : 'expenses'}`}
            </span>
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </footer>
      </section>
    </Modal>
  );
};
